import { RoleGuard } from 'src/guards/role.guard';
import { SharedDirectivesModule } from 'src/modules/shared/shared-directives.module';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AppRoutingModule } from 'src/app-routing.module';
import { SharedComponentsModule } from 'src/modules/shared/shared-components.module';
import { SharedModules } from 'src/modules/shared/shared-modules.module';
import { SharedPipesModule } from 'src/modules/shared/shared-pipes.module';
import { TranslocoRootModule } from 'src/modules/transloco/transloco-root.module';
import { TranslocoService } from '@jsverse/transloco';
import { LanguageSwitcherComponent } from 'src/components/language-switcher/language-switcher.component';
import { AppComponent } from 'src/components/app/app.component';
import { MastheadComponent } from 'src/components/masthead/masthead.component';
import { LoginPageComponent } from 'src/pages/login-page/login-page.component';
import { QueryInvoicesPageComponent } from 'src/pages/query-invoices-page/query-invoices-page.component';
import { StatusMonitorPageComponent } from 'src/pages/status-monitor-page/status-monitor-page.component';
import { AccessDeniedPageComponent } from './pages/access-denied/access-denied-page.component';
import { AuthGuard } from 'src/guards/auth.guard';
import { AuthService } from 'src/services/auth.service';
import { XssInterceptor } from 'src/interceptors/xss-interceptor';

import { UserService } from 'src/services/user.service';
import { AppNav, returnUrlKey } from 'src/routes/app-nav';
import { ApiUrlInterceptor } from 'src/interceptors/api-url.interceptor';
import { RequestAccessPageComponent } from '@/pages/access-management/request-access-page/request-access-page.component';
import { RequestAccessDialogComponent } from '@/components/request-access-dialog/request-access-dialog.component';
import { AccessRequestsPageComponent } from '@/pages/access-management/access-requests-page/access-requests-page.component';
import { ApprovalPageComponent } from '@/pages/access-management/approval-page/approval-page.component';
import { ManageEngagementsPageComponent } from '@/pages/access-management/manage-engagements-page/manage-engagements-page.component';
import { AccessManagementDialogComponent } from '@/components/access-management-dialog/access-management-dialog.component';
import { QueryStatusPipe } from './pipes/query-status.pipe';
import { TransactionQueryPageComponent } from 'src/pages/transaction-query-page/transaction-query-page.component';
import { TransactionQueryStatusPageComponent } from 'src/pages/transaction-query-status-page/transaction-query-status-page.component';
import { TransactionQueryDetailsPageComponent } from 'src/pages/transaction-query-details-page/transaction-query-details-page.component';
import { EMPTY, Observable, map, of, switchMap, tap } from 'rxjs';
import { SpinnerInterceptor } from './interceptors/spinner.interceptor';
import { providePrimeNgConfig } from 'src/primeng-setup';

const loginRequest = {
  scopes: ['User.ReadWrite'],
};

@NgModule({
  declarations: [
    AppComponent,
    LanguageSwitcherComponent,
    LoginPageComponent,
    MastheadComponent,
    QueryInvoicesPageComponent,
    TransactionQueryPageComponent,
    TransactionQueryStatusPageComponent,
    TransactionQueryDetailsPageComponent,
    StatusMonitorPageComponent,
    RequestAccessPageComponent,
    RequestAccessDialogComponent,
    AccessRequestsPageComponent,
    ApprovalPageComponent,
    ManageEngagementsPageComponent,
    AccessManagementDialogComponent,
    QueryStatusPipe,
    AccessDeniedPageComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    RouterModule,
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    SharedModules,
    SharedComponentsModule,
    SharedDirectivesModule,
    SharedPipesModule,
    TranslocoRootModule,
  ],
  providers: [
    AuthGuard,
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [AuthService, UserService, Router, TranslocoService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: XssInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiUrlInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SpinnerInterceptor,
      multi: true,
    },
    RoleGuard,
    provideHttpClient(withInterceptorsFromDi()),
    providePrimeNgConfig(),
  ],
})
export class AppModule {
  constructor(private injector: Injector) {
    AppInjector = this.injector;
  }
}

export let AppInjector: Injector;

export function appInitializerFactory(
  authService: AuthService,
  userService: UserService,
  router: Router,
) {
  return (): Observable<boolean> => {
    if (
      window.location.pathname.startsWith('/' + AppNav.NotAuthenticated.route)
    ) {
      return of(true);
    }

    if (window.location.pathname.indexOf('/swagger') > -1) return of(true);

    return authService.isAuthenticated().pipe(
      switchMap((isAuthenticated) => {
        if (isAuthenticated)
          return userService.initialize$().pipe(
            map(() => {
              const returnUrl = localStorage.getItem(returnUrlKey);
              if (returnUrl) {
                router.navigateByUrl(returnUrl);
                localStorage.removeItem(returnUrlKey);
              }
              return isAuthenticated;
            }),
          );
        else {
          localStorage.setItem(returnUrlKey, window.location.pathname);
          router.navigateByUrl('/' + AppNav.Login.route);
          return of(false);
        }
      }),
    );
  };
}
